import type {
  NotificationCategory,
  NotificationPreference,
  NotificationPreferenceGroup,
} from '@seek/ca-graphql-schema/types';

import type { WithTypeName } from 'src/types/global';

export const categoryUpdates: WithTypeName<NotificationCategory> = {
  code: 'updates',
  description: 'Updates about your applied jobs',
  __typename: 'NotificationCategory',
};

export const categoryOpportunities: WithTypeName<NotificationCategory> = {
  code: 'opportunities',
  description: 'New opportunities for you',
  __typename: 'NotificationCategory',
};

export const categoryMarketing: WithTypeName<NotificationCategory> = {
  code: 'marketing',
  description: 'Marketing communications',
  __typename: 'NotificationCategory',
};

export const categoryCareerHub: WithTypeName<NotificationCategory> = {
  code: 'careerHub',
  description: 'Career Hub',
  __typename: 'NotificationCategory',
};

export const AppViewedSubscription: WithTypeName<NotificationPreference> = {
  product: {
    code: 'AppViewed',
    label: 'Application viewed',
    description:
      'Find out when your job application has been viewed by the employer.',
    __typename: 'NotificationProduct',
  },
  // @ts-ignore
  subscriptions: [
    {
      id: 'AppViewed:email',
      channel: {
        __typename: 'NotificationChannel',
        code: 'email',
        label: 'Email',
      },
      subscribed: true,
      frequency: null,
      __typename: 'NotificationSubscription',
    },
    {
      id: 'AppViewed:push',
      channel: {
        __typename: 'NotificationChannel',
        code: 'push',
        label: 'Push',
      },
      subscribed: false,
      frequency: null,
      __typename: 'NotificationSubscription',
    },
  ],
  __typename: 'NotificationPreference',
};

export const AppConfirmedSubscription: WithTypeName<NotificationPreference> = {
  product: {
    code: 'AppConfirmed',
    label: 'Application confirmation',
    description:
      'Confirmation that your job application has been sent to the employer.',
    __typename: 'NotificationProduct',
  },
  // @ts-ignore
  subscriptions: [
    {
      id: 'AppConfirmed:email',
      channel: {
        code: 'email',
        label: 'Email',
        __typename: 'NotificationChannel',
      },
      subscribed: true,
      frequency: null,
      __typename: 'NotificationSubscription',
    },
  ],
  __typename: 'NotificationPreference',
};

export const AppNotSuitableSubscription: WithTypeName<NotificationPreference> =
  {
    product: {
      code: 'AppNotSuitable',
      label: 'Application unlikely to progress',
      description:
        'Find out if the employer has indicated that your application is unlikely to progress.',
      __typename: 'NotificationProduct',
    },
    // @ts-ignore
    subscriptions: [
      {
        id: 'AppNotSuitable:email',
        channel: {
          code: 'email',
          label: 'Email',
          __typename: 'NotificationChannel',
        },
        subscribed: true,
        frequency: null,
        __typename: 'NotificationSubscription',
      },
      {
        id: 'AppNotSuitable:push',
        channel: {
          code: 'push',
          label: 'Push',
          __typename: 'NotificationChannel',
        },
        subscribed: false,
        frequency: null,
        __typename: 'NotificationSubscription',
      },
    ],
    __typename: 'NotificationPreference',
  };

export const AppAdExpiredSubscription: WithTypeName<NotificationPreference> = {
  product: {
    code: 'AppAdExpired',
    label: 'Job ad expired',
    description:
      'Find out when the job you have applied for is no longer being advertised.',
    __typename: 'NotificationProduct',
  },
  // @ts-ignore
  subscriptions: [
    {
      id: 'AppAdExpired:email',
      channel: {
        code: 'email',
        label: 'Email',
        __typename: 'NotificationChannel',
      },
      subscribed: true,
      frequency: null,
      __typename: 'NotificationSubscription',
    },
  ],
  __typename: 'NotificationPreference',
};

export const AppliedJobsActivitySubscription: WithTypeName<NotificationPreference> =
  {
    product: {
      code: 'AppliedJobsActivity',
      label: 'Applied jobs activity',
      description:
        'Find out if employers have recently processed applications in jobs you applied to.',
      __typename: 'NotificationProduct',
    },
    // @ts-ignore
    subscriptions: [
      {
        id: 'AppliedJobsActivity:email',
        channel: {
          code: 'email',
          label: 'Email',
          __typename: 'NotificationChannel',
        },
        subscribed: true,
        frequency: null,
        __typename: 'NotificationSubscription',
      },
    ],
    __typename: 'NotificationPreference',
  };

export const VerificationRequestsSubscription: WithTypeName<NotificationPreference> =
  {
    product: {
      code: 'VerificationRequests',
      label: 'Verification Requests',
      description:
        'Be notified when there are credentials you can verify to strengthen your application',
      __typename: 'NotificationCTPProduct',
    },
    // @ts-ignore
    subscriptions: [
      {
        id: 'VerificationRequests:email',
        channel: {
          code: 'email',
          label: 'Email',
          __typename: 'NotificationCTPChannel',
        },
        subscribed: true,
        frequency: null,
        __typename: 'NotificationCTPSubscription',
      },
    ],
    __typename: 'NotificationCTPPreference',
  };

export const notificationSubscriptionFrequencyOptions: Array<{
  __typename?: 'NotificationFrequencyOptions' | undefined;
  code: string;
  label: string;
  description: string | null;
}> = [
  {
    code: 'dynamic',
    label: 'Dynamic',
    description:
      'More frequent emails when you’re actively looking and less when you’re not.',
    __typename: 'NotificationFrequencyOptions',
  },
  {
    code: 'daily',
    label: 'Daily',
    description: null,
    __typename: 'NotificationFrequencyOptions',
  },
  {
    code: 'weekly',
    label: 'Weekly',
    description: null,
    __typename: 'NotificationFrequencyOptions',
  },
];

export const notificationPreferenceGroups: WithTypeName<
  NotificationPreferenceGroup[]
> = [
  {
    __typename: 'NotificationPreferenceGroup',
    category: categoryUpdates,
    // @ts-ignore
    notificationPreferences: [
      AppConfirmedSubscription,
      AppViewedSubscription,
      AppNotSuitableSubscription,
      AppAdExpiredSubscription,
      AppliedJobsActivitySubscription,
      VerificationRequestsSubscription,
    ],
  },
  {
    __typename: 'NotificationPreferenceGroup',
    category: categoryOpportunities,
    notificationPreferences: [
      {
        product: {
          code: 'WRU',
          label: 'Weekly job recommendations',
          description:
            'SEEK Weekly Round Up features job recommendations and industry insights based on your recent activity and SEEK Profile.',
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'WRU:email',
            channel: {
              code: 'email',
              label: 'Email',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: {
              value: 'dynamic',
              // @ts-ignore
              options: notificationSubscriptionFrequencyOptions,
              __typename: 'NotificationFrequency',
            },
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
      {
        product: {
          code: 'NewJob',
          label: 'Jobs just for you',
          description:
            'Get the latest jobs recommended for you, sent straight to your mobile.',
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'NewJob:push',
            channel: {
              code: 'push',
              label: 'Push',
              __typename: 'NotificationChannel',
            },
            subscribed: false,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
      {
        product: {
          code: 'ReminderToApply',
          label: 'Reminder to apply',
          description:
            "If you save a job but don't apply for it within the week, we'll send a reminder via push notification.",
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'ReminderToApply:push',
            channel: {
              code: 'push',
              label: 'Push',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
          {
            id: 'ReminderToApply:email',
            channel: {
              code: 'email',
              label: 'Email',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
      {
        product: {
          code: 'SavedSearch',
          label: 'Saved search job alerts',
          description: 'The latest jobs that match your saved searches',
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'SavedSearch:email',
            channel: {
              code: 'email',
              label: 'Email',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
    ],
  },
  {
    __typename: 'NotificationPreferenceGroup',
    category: categoryCareerHub,
    notificationPreferences: [
      {
        product: {
          code: 'CareerHubThreadReply',
          label: 'Activity updates',
          description: "Find out when there's new activity on your threads.",
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'CareerHubThreadReply:push',
            channel: {
              code: 'push',
              label: 'Push',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
      {
        product: {
          code: 'CareerHubReengageExplore',
          label: 'Content just for you',
          description:
            "We'll notify you when we match career content to your needs.",
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'CareerHubReengageExplore:push',
            channel: {
              code: 'push',
              label: 'Push',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
    ],
  },

  {
    __typename: 'NotificationPreferenceGroup',
    category: categoryMarketing,
    notificationPreferences: [
      {
        product: {
          code: 'MarketingAdviceTips',
          label: 'Advice & Tips',
          description:
            "Get the latest career advice, industry trends and tips to get you where you're going.",
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'MarketingAdviceTips:push',
            channel: {
              code: 'push',
              label: 'Push',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
      {
        product: {
          code: 'MarketingProfileUpdate',
          label: 'SEEK Profile',
          description:
            'Reminders to keep your SEEK Profile up to date to help find your next opportunity.',
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'MarketingProfileUpdate:push',
            channel: {
              code: 'push',
              label: 'Push',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
      {
        product: {
          code: 'MarketingSurveyCompetitions',
          label: 'Surveys & competitions',
          description:
            'Share your experience to help us learn more about what you want from SEEK and be in it to win with SEEK competitions.',
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'MarketingSurveyCompetitions:push',
            channel: {
              code: 'push',
              label: 'Push',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
    ],
  },
];
